import React, { useState } from 'react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * Admin → Notifications → Send now.
 * Pick people (and an optional copy line) and send that email immediately.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Chip({ person, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 12, padding: '3px 8px', borderRadius: 999,
      background: '#fff', border: '1px solid #dee2e6', color: '#334155',
    }} title={person.email}>
      {person.label || person.email}
      <button
        type="button"
        onClick={onRemove}
        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 13, padding: 0, lineHeight: 1 }}
        title="Remove"
      >×</button>
    </span>
  );
}

const NotificationSendNowModal = ({ feature, instance, accounts, onClose }) => {
  const available = feature?.adhoc?.available === true;
  const note = feature?.adhoc?.note || '';
  const reason = feature?.adhoc?.reason || 'This email cannot be sent from here.';

  const [toList, setToList] = useState([]);
  const [ccList, setCcList] = useState([]);
  const [draft, setDraft] = useState('');
  const [ccDraft, setCcDraft] = useState('');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const addPerson = (list, setList, email, label) => {
    const key = String(email || '').toLowerCase().trim();
    if (!EMAIL_RE.test(key)) {
      setError('Enter a full email address');
      return;
    }
    setError(null);
    setList((prev) => (prev.some((p) => p.email === key) ? prev : [...prev, { email: key, label: label || key }]));
  };

  const useRoster = () => {
    const people = (feature.recipients || [])
      .filter((r) => r.email)
      .map((r) => ({ email: String(r.email).toLowerCase(), label: r.displayName || r.email }));
    setToList((prev) => {
      const seen = new Set(prev.map((p) => p.email));
      return [...prev, ...people.filter((p) => !seen.has(p.email))];
    });
  };

  const runSearch = async (value) => {
    setSearch(value);
    if (!value.trim()) { setResults([]); return; }
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'get',
        `/notification-preferences/admin/users?search=${encodeURIComponent(value.trim())}`
      );
      setResults(res.data.users || []);
    } catch (err) {
      setResults([]);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    setDone(null);
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'post',
        '/notification-preferences/admin/send-now',
        { feature: feature.feature, to: toList.map((p) => p.email), cc: ccList.map((p) => p.email) },
        { timeout: 300000 }
      );
      setDone(res.data);
    } catch (err) {
      const timedOut = err.code === 'ECONNABORTED';
      setError(timedOut
        ? 'The browser stopped waiting. Check Email Log — the send may still finish.'
        : (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)', maxHeight: '90vh', overflow: 'auto',
          background: '#fff', borderRadius: 10, padding: '16px 18px 18px',
          boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, color: '#0f766e', fontWeight: 700, letterSpacing: 0.3 }}>SEND NOW</div>
            <h3 style={{ margin: '2px 0 0', fontSize: 16, color: '#0f172a' }}>{feature.label}</h3>
          </div>
          <button type="button" onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {!available ? (
          <p style={{ margin: '14px 0 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{reason}</p>
        ) : (
          <>
            {note && <p style={{ margin: '10px 0 0', fontSize: 13, color: '#475569', lineHeight: 1.45 }}>{note}</p>}

            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Who gets it</label>
                {(feature.recipients || []).length > 0 && (
                  <button type="button" onClick={useRoster} style={{ border: 'none', background: 'none', color: '#0f766e', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Start from the list
                  </button>
                )}
              </div>
              <input
                value={search}
                onChange={(e) => runSearch(e.target.value)}
                placeholder="Search a portal user…"
                style={{ width: '100%', marginTop: 6, padding: '8px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 13, boxSizing: 'border-box' }}
              />
              {results.length > 0 && (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, marginTop: 4, maxHeight: 140, overflow: 'auto' }}>
                  {results.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        addPerson(toList, setToList, u.email, u.display_name || u.email);
                        setSearch('');
                        setResults([]);
                      }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', padding: '6px 10px', cursor: 'pointer' }}
                    >
                      <div style={{ fontSize: 13, color: '#0f172a' }}>{u.display_name || u.email}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{u.email}</div>
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPerson(toList, setToList, draft, draft);
                      setDraft('');
                    }
                  }}
                  placeholder="Or type an email, including a shared mailbox"
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => { addPerson(toList, setToList, draft, draft); setDraft(''); }}
                  style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '0 10px', cursor: 'pointer', fontSize: 12 }}
                >Add</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {toList.map((p) => (
                  <Chip key={p.email} person={p} onRemove={() => setToList((prev) => prev.filter((x) => x.email !== p.email))} />
                ))}
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Copy (optional)</label>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input
                  value={ccDraft}
                  onChange={(e) => setCcDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPerson(ccList, setCcList, ccDraft, ccDraft);
                      setCcDraft('');
                    }
                  }}
                  placeholder="Email to copy"
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => { addPerson(ccList, setCcList, ccDraft, ccDraft); setCcDraft(''); }}
                  style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6, padding: '0 10px', cursor: 'pointer', fontSize: 12 }}
                >Add</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {ccList.map((p) => (
                  <Chip key={p.email} person={p} onRemove={() => setCcList((prev) => prev.filter((x) => x.email !== p.email))} />
                ))}
              </div>
            </div>

            {error && <p style={{ margin: '12px 0 0', fontSize: 13, color: '#b91c1c' }}>{error}</p>}
            {done && (
              <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, color: '#14532d' }}>
                <div>{done.message}</div>
                {(done.skipped || []).length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {done.skipped.map((s) => (
                      <li key={s.email}>{s.email}: {s.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={onClose} style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}>Close</button>
              <button
                type="button"
                disabled={sending || toList.length === 0}
                onClick={send}
                style={{
                  border: 'none', borderRadius: 6, padding: '8px 14px', cursor: sending || toList.length === 0 ? 'default' : 'pointer',
                  background: '#0f766e', color: '#fff', fontWeight: 700, opacity: sending || toList.length === 0 ? 0.6 : 1,
                }}
              >{sending ? 'Sending…' : 'Send now'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSendNowModal;
